import React, { useState } from 'react'
import { ExternalLink, FileText, Shield, X } from 'lucide-react'

export interface VerificationDocumentItem {
  url: string
  type: string
}

const DOC_TYPE_LABELS: Record<string, string> = {
  businessReg: 'Business Registration',
  gst: 'GST Certificate',
  tradeLicense: 'Trade License',
  ownerId: 'Owner ID',
  addressProof: 'Address Proof',
  yogaAlliance: 'Yoga Alliance Certificate',
  additional: 'Additional Document',
}

function getDocLabel(type: string): string {
  return DOC_TYPE_LABELS[type] || type.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())
}

function isImageUrl(url: string): boolean {
  return /\.(png|jpe?g|gif|webp)(\?|$)/i.test(url)
}

function isPdfUrl(url: string): boolean {
  return /\.pdf(\?|$)/i.test(url)
}

interface VerificationDocumentsListProps {
  documents: VerificationDocumentItem[]
  emptyMessage?: string
}

const VerificationDocumentsList: React.FC<VerificationDocumentsListProps> = ({
  documents,
  emptyMessage = 'No documents on file yet.',
}) => {
  const [previewImage, setPreviewImage] = useState<string | null>(null)

  if (documents.length === 0) {
    return <p className="text-sm text-gray-500">{emptyMessage}</p>
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {documents.map((doc, index) => {
          const isImage = isImageUrl(doc.url)
          const isPdf = isPdfUrl(doc.url)
          return (
            <div
              key={`${doc.url}-${index}`}
              className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm"
            >
              <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center overflow-hidden">
                {isImage ? (
                  <button
                    type="button"
                    onClick={() => setPreviewImage(doc.url)}
                    className="h-full w-full focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-inset"
                  >
                    <img
                      src={doc.url}
                      alt={getDocLabel(doc.type)}
                      className="h-full w-full object-cover hover:opacity-95 transition-opacity"
                    />
                  </button>
                ) : (
                  <div className="flex flex-col items-center gap-2 px-4 text-center text-gray-500">
                    {isPdf ? (
                      <FileText className="h-12 w-12 text-red-500/80" aria-hidden />
                    ) : (
                      <Shield className="h-10 w-10" aria-hidden />
                    )}
                    <span className="text-sm font-medium">
                      {isPdf ? 'PDF document' : 'Preview not available'}
                    </span>
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-gray-100">
                <p className="text-sm font-semibold text-gray-900">{getDocLabel(doc.type)}</p>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 hover:underline"
                  >
                    {isPdf ? 'Open PDF' : 'Open file'}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  {isImage && (
                    <button
                      type="button"
                      onClick={() => setPreviewImage(doc.url)}
                      className="text-sm font-medium text-gray-600 hover:text-gray-900"
                    >
                      Preview
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {previewImage && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setPreviewImage(null)}
        >
          <button
            type="button"
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Close preview"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={previewImage}
            alt="Document preview"
            className="max-h-[90vh] max-w-full rounded-lg shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  )
}

export default VerificationDocumentsList
